import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';

import { ReactComponent as AlertV1Icon } from '../../../assets/icons/alert-v1-icon.svg';
import { ReactComponent as NavigateBack } from '../../../assets/icons/arrow-left.svg';
import { ReactComponent as ClockIcon } from '../../../assets/icons/clock-icon.svg';
import { ReactComponent as EnergyIcon } from '../../../assets/icons/energy-icon.svg';
import { ReactComponent as PaymentIcon } from '../../../assets/icons/payment-icon.svg';
import { ReactComponent as RefreshIcon } from '../../../assets/icons/refresh-icon.svg';
import { ReactComponent as TimerIcon } from '../../../assets/icons/timer-icon.svg';
import { ReactComponent as UnplugIcon } from '../../../assets/icons/unplug-icon.svg';
import ChargerBg from '../../../assets/images/charger-bg.png';
import { AdhocRemoteStartPayload } from '../../../interface/charger.interface';
import BatteryLoader from '../../../shared-components/battery-loader/BatteryLoader';
import MyButton from '../../../shared-components/button/button';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import ToastMessage from '../../../shared-components/toast-message/ToastMessage';
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from '../../../shared-functions/LocalStorage';
import charger from '../../../utils/api/charger';

import ChargingSpeedSocChart from './ChargingSpeedSocChart';
import PreChargeConfig, { ChargeLimitConfig } from './PreChargeConfig';

interface ConnectorInterface {
  _id: string;
  connector: string;
  connectorId: string | number;
  isReservationEnabled: boolean;
  status: string;
  capacity?: string;
}

interface ChargingStatusProperties {
  paymentDetails: any;
  navigateToPaymentMethod: (removeQueryParameters?: boolean) => void | Promise<void>;
  navigateToInvoice: () => void;
  chargingStatus: boolean;
  connectors: ConnectorInterface[];
  skipChargingButton?: boolean;
  isFreeCharging?: boolean;
  currentPrice: string;
  vat?: number | string;
  appVersion: string;
  fees?: {
    platformFee: number;
    platformFeeType: string;
    sessionFee: number;
    sessionFeeType: string;
    currencyType?: string;
    vat?: number | string;
  } | null;
  onPaymentRequired?: () => void;
}

interface LiveCostWatts {
  liveCost: number;
  liveWatts: number;
  currency: string;
  liveTime: {
    hours: number;
    minutes: number;
    seconds: number;
  };
}

interface DurationAmount {
  from: number;
  to: number;
  mode: 'grace' | 'flatRate' | 'perMinute';
  amount: number;
}

interface SocKwApiPoint {
  ts: string;
  kw: number;
  soc: number;
}

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
}

/**
 * Gets elapsed minutes from timestamp
 * @param startTime ISO timestamp
 * @returns Elapsed minutes
 */
const getElapsedMinutes = (startTime: string) => {
  const start = new Date(startTime).getTime();
  const now = Date.now();
  return Math.floor((now - start) / 60000);
};

/**
 * Calculates effective ceiling value
 * @param ceilingType Ceiling calculation type
 * @param ceilingValue Ceiling value
 * @param totalAmount Total transaction amount
 * @returns Effective ceiling
 */
const getEffectiveCeilingValue = (
  ceilingType: 'fixedAmount' | 'percentage',
  ceilingValue: number,
  totalAmount: number,
) => {
  if (ceilingType === 'percentage') {
    return ceilingValue * totalAmount;
  }
  return ceilingValue;
};

/**
 * Calculates total idle fee amount based on slabs
 * @param elapsedMinutes Elapsed idle minutes
 * @param slabs Idle duration amount slabs
 * @param ceilingType Ceiling type
 * @param ceilingValue Ceiling value
 * @param totalAmount Total amount
 * @returns Computed total fee
 */
const calculateIdleFeeAmount = (
  elapsedMinutes: number,
  slabs: DurationAmount[],
  ceilingType: 'fixedAmount' | 'percentage',
  ceilingValue: number,
  totalAmount: number,
) => {
  const effectiveCeiling = getEffectiveCeilingValue(ceilingType, ceilingValue, totalAmount);
  let totalFee = 0;

  slabs.some((slab) => {
    if (elapsedMinutes < slab.from) {
      return true;
    }
    if (slab.mode === 'flatRate') {
      totalFee += slab.amount;
    }
    if (slab.mode === 'perMinute') {
      const maxMinutes = slab.to - slab.from + 1;
      const usedMinutes = Math.min(elapsedMinutes - slab.from + 1, maxMinutes);
      if (usedMinutes > 0) {
        totalFee += usedMinutes * slab.amount;
      }
    }
    if (totalFee >= effectiveCeiling) {
      totalFee = effectiveCeiling;
      return true;
    }
    return false;
  });

  return totalFee;
};

/**
 * Gets current active idle mode
 * @param elapsedMinutes Elapsed minutes
 * @param slabs Slabs list
 * @returns Mode name
 */
const getCurrentIdleMode = (elapsedMinutes: number, slabs: DurationAmount[]) => {
  const slab = slabs.find((item) => elapsedMinutes >= item.from && elapsedMinutes < item.to);
  return slab ? slab.mode : '';
};

/**
 * Gets localized label for idle mode
 * @param mode Idle mode string
 * @param t Translation function
 * @returns Localized label
 */
const getIdleModeLabel = (mode: string, t: any) => {
  switch (mode) {
    case 'grace':
      return t('gracePeriod');
    case 'flatRate':
      return t('flatRate');
    case 'perMinute':
      return t('perMinute');
    default:
      return '';
  }
};

/**
 * ChargingStatus Component
 * @param properties Component properties
 * @returns JSX Element
 */
function ChargingStatus(properties: ChargingStatusProperties): JSX.Element {
  const { t } = useTranslation('charger');
  const {
    paymentDetails,
    navigateToPaymentMethod,
    navigateToInvoice,
    chargingStatus,
    connectors,
    currentPrice,
    vat,
    appVersion,
    fees,
    isFreeCharging,
    onPaymentRequired,
  } = properties;

  const [actionRequired, setActionRequired] = useState(false);
  const [startCharging, setStartCharging] = useState<boolean>(chargingStatus);
  const [loader, setLoader] = useState<boolean>(false);
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const [connectionTimer, setConnectionTimer] = useState<number>(90);
  const [isConnectionTimeout, setIsConnectionTimeout] = useState<boolean>(false);
  const [selectedConnectorId, setSelectedConnectorId] = useState<string | number>('');
  const { deviceId: routeDeviceId } = useParams<{ deviceId: string }>();

  const location = useLocation();
  const queryParameters = new URLSearchParams(location.search);
  const deviceId = routeDeviceId || queryParameters.get('deviceId') || '';
  const domainParameter = queryParameters.get('domain');

  const [toastStatus, setToastStatus] = useState({
    openToast: false,
    showToastMessage: '',
  });

  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: true,
  });

  const [liveCostWatts, setLiveCostWatts] = useState<LiveCostWatts>({
    liveCost: 0,
    liveWatts: 0,
    currency: fees?.currencyType || 'SAR',
    liveTime: { hours: 0, minutes: 0, seconds: 0 },
  });

  const [refreshLoading, setRefreshLoading] = useState<boolean>(false);
  const [idleElapsedTime, setIdleElapsedTime] = useState('00:00:00');
  const [idleAmount, setIdleAmount] = useState(0);
  const [idleMode, setIdleMode] = useState('');
  const [ceilingValue, setCeilingValue] = useState<number>(0);
  const [durationAmountList, setDurationAmountList] = useState<DurationAmount[]>([]);
  const [penaltyTransactionStartTime, setPenaltyTransactionStartTime] = useState<string | null>(
    null,
  );
  const [ceilingType, setCeilingType] = useState<'fixedAmount' | 'percentage'>('fixedAmount');
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [realTimeChargingData, setRealTimeChargingData] = useState<SocKwApiPoint[]>([]);
  const [isCommandAccepted, setIsCommandAccepted] = useState<boolean>(chargingStatus);
  const [isStopping, setIsStopping] = useState<boolean>(false);
  const commandPollTimerReference = useRef<NodeJS.Timeout | null>(null);
  const stopPollTimerReference = useRef<NodeJS.Timeout | null>(null);
  const isCancelledReference = useRef<boolean>(false);
  const hasActiveSessionStartedReference = useRef<boolean>(false);
  const liveCostWattsReference = useRef<LiveCostWatts>(liveCostWatts);
  useEffect(() => {
    liveCostWattsReference.current = liveCostWatts;
  }, [liveCostWatts]);

  useEffect(() => {
    isCancelledReference.current = false;
    return () => {
      isCancelledReference.current = true;
      if (commandPollTimerReference.current) {
        clearTimeout(commandPollTimerReference.current);
        commandPollTimerReference.current = null;
      }
      if (stopPollTimerReference.current) {
        clearTimeout(stopPollTimerReference.current);
        stopPollTimerReference.current = null;
      }
    };
  }, []);

  /**
   * Handles navigation back to start screen for both free and paid charging flows.
   * Resets connection/charging states, clears timers, closes modals, and triggers session reset.
   */
  const handleBackToStart = async () => {
    setModalBoxStatus('close');
    setLoader(false);
    setIsConnecting(false);
    setIsConnectionTimeout(false);
    setIsCommandAccepted(false);
    setIsStopping(false);
    setStartCharging(false);
    setConnectionTimer(90);
    setActionRequired(false);
    hasActiveSessionStartedReference.current = false;

    if (commandPollTimerReference.current) {
      clearTimeout(commandPollTimerReference.current);
      commandPollTimerReference.current = null;
    }
    if (stopPollTimerReference.current) {
      clearTimeout(stopPollTimerReference.current);
      stopPollTimerReference.current = null;
    }

    removeLocalStorageItem('commandId');
    removeLocalStorageItem('chargingStatus');
    removeLocalStorageItem('connectionStartTime');
    removeLocalStorageItem('sessionId');
    removeLocalStorageItem('guestToken');

    await navigateToPaymentMethod(true);
  };

  /**
   * Shows error modal dialog with thrown message and navigation button back to start screen
   * @param errorMessage Error message to display
   * @param errorTitle Optional error title
   */
  const showErrorModal = (errorMessage: string, errorTitle?: string) => {
    setLoader(false);
    setIsConnecting(false);
    setIsCommandAccepted(false);
    setIsStopping(false);
    if (commandPollTimerReference.current) {
      clearTimeout(commandPollTimerReference.current);
      commandPollTimerReference.current = null;
    }
    if (stopPollTimerReference.current) {
      clearTimeout(stopPollTimerReference.current);
      stopPollTimerReference.current = null;
    }
    removeLocalStorageItem('commandId');
    removeLocalStorageItem('chargingStatus');
    removeLocalStorageItem('connectionStartTime');

    setModalBoxStatus('open');
    setModalBoxDetail({
      title: '',
      content: (
        <div className="modal-error-message">
          <div className="error-content">
            <span>
              <AlertV1Icon />
            </span>
            <p>{errorTitle || t('chargingStartFailed') || 'Unable to Start Charging'}</p>
          </div>
          <p className="description">
            {errorMessage || t('somethingWrong') || 'Something went wrong. Please try again.'}
          </p>
          <div className="modal-button-wrap">
            <MyButton
              label={t('goToStartScreen') || 'Back to Start'}
              buttonType="submit"
              onClickFunc={handleBackToStart}
            />
          </div>
        </div>
      ),
      notificationModal: true,
    });
  };

  /**
   * Polls adhoc command status until accepted, rejected, or timeout
   * Endpoint: GET /emsp/api/v1/adhoc/command/:command_id/status
   * @param targetCommandId Identifier of command
   */
  const checkCommandStatus = (targetCommandId: string | number) => {
    if (commandPollTimerReference.current) {
      clearTimeout(commandPollTimerReference.current);
      commandPollTimerReference.current = null;
    }

    const maxAttempts = 45; // 45 * 2s = 90s
    const pollInterval = 2000; // Poll every 2 seconds
    let attempts = 0;
    let consecutiveErrors = 0;

    /**
     * Internal poll function for command status
     */
    const poll = async () => {
      if (isCancelledReference.current) {
        return;
      }

      try {
        const response = await charger.getCommandStatus(targetCommandId);
        consecutiveErrors = 0;
        const statusData = response?.data?.data || response?.data;

        const currentStatus = String(statusData?.status || '').toLowerCase();
        const currentResult = String(statusData?.result || '').toLowerCase();

        // 1. Success condition: status === 'accept' (or 'accepted')
        if (
          currentStatus === 'accept' ||
          currentStatus === 'accepted' ||
          currentResult === 'accepted' ||
          currentResult === 'accept'
        ) {
          setIsCommandAccepted(true);
          return;
        }

        // 2. Failure condition: status/result === 'rejected', 'failed', 'not_supported'
        if (
          currentStatus === 'rejected' ||
          currentStatus === 'failed' ||
          currentResult === 'rejected' ||
          currentResult === 'failed' ||
          currentResult === 'not_supported'
        ) {
          showErrorModal(
            statusData?.message || t('commandRejected') || 'Command was rejected by charger.',
          );
          return;
        }

        // 3. In-progress condition: pending, in_progress, sent, etc.
        attempts += 1;
        if (attempts >= maxAttempts) {
          showErrorModal(
            t('sessionStartTimeoutDesc') ||
              'The charger did not respond within 90 seconds. Please verify that the charging connector is firmly plugged in and try again.',
            t('sessionStartTimeout') || 'Charger Did Not Start',
          );
          return;
        }

        commandPollTimerReference.current = setTimeout(poll, pollInterval);
      } catch (error: any) {
        consecutiveErrors += 1;
        const errorData = error?.response?.data;
        const errorMessage =
          errorData?.message ||
          errorData?.error ||
          error?.message ||
          t('somethingWrong') ||
          'Something went wrong';

        const statusCode = error?.response?.status;
        if (
          statusCode === 400 ||
          statusCode === 401 ||
          statusCode === 403 ||
          statusCode === 404 ||
          consecutiveErrors >= 3
        ) {
          showErrorModal(errorMessage);
          return;
        }

        attempts += 1;
        if (attempts >= maxAttempts) {
          showErrorModal(errorMessage);
          return;
        }

        commandPollTimerReference.current = setTimeout(poll, pollInterval);
      }
    };

    poll();
  };

  // Restore connection timer if page was refreshed during 90s connection window
  useEffect(() => {
    const savedStartTime = getLocalStorageItem('connectionStartTime');
    if (savedStartTime && !startCharging) {
      const elapsed = Math.floor((Date.now() - Number(savedStartTime)) / 1000);
      if (elapsed < 90) {
        setConnectionTimer(90 - elapsed);
        setIsConnecting(true);
        setIsConnectionTimeout(false);
        const savedCommandId = getLocalStorageItem('commandId');
        if (savedCommandId) {
          checkCommandStatus(savedCommandId);
        } else {
          setIsCommandAccepted(true);
        }
      } else {
        setIsConnecting(false);
        setIsConnectionTimeout(true);
        setConnectionTimer(0);
      }
    }
  }, [startCharging]);

  // 90-second countdown timer using wall-clock time (resilient to browser tab sleep/backgrounding)
  useEffect(() => {
    if (!isConnecting) {
      return undefined;
    }

    /**
     * Updates connection countdown using wall-clock time
     */
    const updateCountdown = () => {
      const savedStartTime = getLocalStorageItem('connectionStartTime');
      const start = savedStartTime ? Number(savedStartTime) : Date.now();
      const elapsed = Math.floor((Date.now() - start) / 1000);
      const remaining = Math.max(0, 90 - elapsed);

      if (remaining <= 0) {
        if (commandPollTimerReference.current) {
          clearTimeout(commandPollTimerReference.current);
          commandPollTimerReference.current = null;
        }
        setIsConnecting(false);
        setIsConnectionTimeout(true);
        setIsCommandAccepted(false);
        removeLocalStorageItem('connectionStartTime');
        removeLocalStorageItem('commandId');
        setConnectionTimer(0);
      } else {
        setConnectionTimer(remaining);
      }
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);

    return () => clearInterval(timer);
  }, [isConnecting]);

  /**
   * Resets connection state and navigates back to try again
   */
  const handleRestartConnection = () => {
    handleBackToStart();
  };

  /**
   * Handles start charging API invocation
   * @param chargeLimit Optional configured limit
   */
  const handleStartCharging = async (chargeLimit?: ChargeLimitConfig) => {
    const targetConnectorId =
      selectedConnectorId || (connectors.length > 0 ? connectors[0].connectorId : 1);
    const singleConnector =
      connectors.find((connector) => connector.connectorId === targetConnectorId) || connectors[0];

    if (singleConnector) {
      setLoader(true);
      const locationId = (singleConnector as any)?.locationId || '';
      const evseUid = deviceId || '';

      let chargeType: string | undefined;
      let enteredValue: number | undefined;

      if (chargeLimit && chargeLimit.value) {
        if (chargeLimit.type === 'battery') {
          chargeType = 'percentage';
        } else {
          chargeType = chargeLimit.type;
        }
        enteredValue = Number(chargeLimit.value);
      }

      const currentSoc =
        chargeLimit?.currentSoc !== undefined && chargeLimit?.currentSoc !== null && chargeLimit?.currentSoc !== ''
          ? Number(chargeLimit.currentSoc)
          : undefined;
      const batteryCapacityKwh =
        chargeLimit?.batteryCapacityKwh !== undefined && chargeLimit?.batteryCapacityKwh !== null && chargeLimit?.batteryCapacityKwh !== ''
          ? Number(chargeLimit.batteryCapacityKwh)
          : undefined;

      const request: AdhocRemoteStartPayload = {
        locationId,
        evseUid,
        connectorId: String(singleConnector.connectorId),
        ...(chargeType && enteredValue !== undefined && !Number.isNaN(enteredValue)
          ? {
              chargeType,
              enteredValue,
              ...(currentSoc !== undefined && !Number.isNaN(currentSoc)
                ? {
                    currentSoc,
                  }
                : {}),
              ...(batteryCapacityKwh !== undefined && !Number.isNaN(batteryCapacityKwh)
                ? {
                    batteryCapacityKwh,
                  }
                : {}),
            }
          : {}),
      };

      try {
        const response = await charger.adhocRemoteStart(request);
        const responseData = response?.data?.data || response?.data;

        if (
          responseData?.customCode === 'PAYMENT_REGISTRATION_REQUIRED' ||
          responseData?.data?.customCode === 'PAYMENT_REGISTRATION_REQUIRED' ||
          responseData?.message?.includes('Payment registration is required') ||
          responseData?.data?.message?.includes('Payment registration is required')
        ) {
          setLoader(false);
          setIsConnecting(false);
          if (onPaymentRequired) {
            onPaymentRequired();
          } else {
            navigateToPaymentMethod(false);
          }
          return;
        }

        if (
          responseData?.result === 'EVSE_INOPERATIVE' ||
          responseData?.status === 'EVSE_INOPERATIVE'
        ) {
          showErrorModal(
            responseData?.message ||
              'Please plug in the connector before starting the charging session.',
          );
          return;
        }

        if (
          responseData?.result === 'REJECTED' ||
          responseData?.status === 'REJECTED' ||
          responseData?.result === 'NOT_SUPPORTED'
        ) {
          showErrorModal(responseData?.message || t('somethingWrong'));
          return;
        }

        const commandId =
          responseData?.commandId ??
          responseData?.command_id ??
          responseData?.data?.commandId ??
          responseData?.data?.command_id;

        if (
          responseData?.result === 'ACCEPTED' ||
          responseData?.result === 'SUCCESS' ||
          responseData?.result === 'OK' ||
          responseData?.status === 'ACCEPTED' ||
          responseData?.status === 'inProgress' ||
          commandId
        ) {
          window.history.replaceState(
            {},
            '',
            domainParameter
              ? `${window.location.pathname}?domain=${domainParameter}`
              : window.location.pathname,
          );
          setLoader(false);
          setLocalStorageItem('chargingStatus', 'inProgress');
          setLocalStorageItem('connectionStartTime', Date.now().toString());
          hasActiveSessionStartedReference.current = false;
          setConnectionTimer(90);
          setIsConnectionTimeout(false);
          setIsConnecting(true);

          if (commandId) {
            setLocalStorageItem('commandId', String(commandId));
            setIsCommandAccepted(false);
            checkCommandStatus(commandId);
          } else {
            setIsCommandAccepted(true);
          }
          return;
        }

        showErrorModal(responseData?.message || t('somethingWrong'));
      } catch (error: any) {
        const errorData = error?.response?.data;
        const customCode = errorData?.customCode || errorData?.data?.customCode;
        const errorMessage =
          errorData?.message ||
          errorData?.data?.message ||
          errorData?.error ||
          error?.message ||
          t('somethingWrong') ||
          'Failed to start charging';

        if (
          customCode === 'PAYMENT_REGISTRATION_REQUIRED' ||
          errorMessage.includes('Payment registration is required')
        ) {
          setLoader(false);
          setIsConnecting(false);
          if (onPaymentRequired) {
            onPaymentRequired();
          } else {
            navigateToPaymentMethod(false);
          }
          return;
        }

        showErrorModal(errorMessage);
      }
    } else {
      setToastStatus({
        openToast: true,
        showToastMessage: t('selectConnector'),
      });
    }
  };

  /**
   * Handles completed charging session stop
   * @param sessionData Session payload
   */
  const handleChargingStop = useCallback(
    (sessionData: any) => {
      if (commandPollTimerReference.current) {
        clearTimeout(commandPollTimerReference.current);
        commandPollTimerReference.current = null;
      }
      if (stopPollTimerReference.current) {
        clearTimeout(stopPollTimerReference.current);
        stopPollTimerReference.current = null;
      }
      setIsStopping(false);
      setIsCommandAccepted(false);
      removeLocalStorageItem('selectedConnector');
      removeLocalStorageItem('guestToken');
      removeLocalStorageItem('chargingStatus');
      removeLocalStorageItem('paymentMethod');
      removeLocalStorageItem('connectionStartTime');
      removeLocalStorageItem('commandId');

      if (sessionData?.sessionId) {
        setLocalStorageItem('sessionId', sessionData.sessionId);
      }

      const currentLive = liveCostWattsReference.current;
      const rawPaid =
        sessionData?.paidAmount ?? sessionData?.liveCost ?? sessionData?.totalCost ?? currentLive.liveCost;
      const parsedPaid = Number(rawPaid);
      let finalPaid = 0;
      if (!isFreeCharging && Number.isFinite(parsedPaid)) {
        finalPaid = parsedPaid;
      }

      const rawKwh =
        sessionData?.powerConsumed ??
        sessionData?.totalKwh ??
        sessionData?.energyKwh ??
        currentLive.liveWatts;
      const parsedKwh = Number(rawKwh);
      const finalKwh = Number.isFinite(parsedKwh) ? parsedKwh : 0;
      const hours = sessionData?.liveTime?.hours ?? currentLive.liveTime.hours;
      const minutes = sessionData?.liveTime?.minutes ?? currentLive.liveTime.minutes;
      const seconds = sessionData?.liveTime?.seconds ?? currentLive.liveTime.seconds;
      const currency =
        sessionData?.billingCurrency || sessionData?.currency || currentLive.currency || 'SAR';

      setLocalStorageItem('paidAmount', finalPaid);
      setLocalStorageItem('totalKwhConsumer', finalKwh);
      setLocalStorageItem('liveHours', hours);
      setLocalStorageItem('liveMinutes', minutes);
      setLocalStorageItem('liveSeconds', seconds);
      setLocalStorageItem('currency', currency);
      navigateToInvoice();
    },
    [navigateToInvoice],
  );

  /**
   * Appends real-time SOC data point
   * @param point SOC point
   */
  const appendSocPoint = useCallback((point: SocKwApiPoint) => {
    setRealTimeChargingData((previous) => {
      const index = previous.findIndex((item) => item.ts === point.ts);
      if (index !== -1) {
        const updated = [...previous];
        updated[index] = point;
        return updated;
      }
      return [...previous, point];
    });
  }, []);

  // Polling for live commercial charging transaction every 5 seconds (when command is accepted or session is active)
  useEffect(() => {
    if (!startCharging && !isCommandAccepted) {
      return undefined;
    }

    let pollingTimer: NodeJS.Timeout | null = null;

    /**
     * Polls live commercial charging transaction data
     */
    const pollLiveCommercialData = async () => {
      try {
        const response = await charger.getTransaction({});
        console.log(response, 'response...');

        const rawData = response?.data?.data || response?.data;

        // When active is null, only proceed to invoice if the session had already started/been active
        if (
          rawData?.active === null ||
          (rawData && 'active' in rawData && rawData.active === null)
        ) {
          if (hasActiveSessionStartedReference.current) {
            hasActiveSessionStartedReference.current = false;
            setStartCharging(false);
            setIsConnecting(false);
            setIsCommandAccepted(false);
            handleChargingStop(rawData?.summary || rawData?.lastSession || rawData);
          }
          return;
        }

        const activeData = rawData?.active || rawData;

        if (activeData) {
          hasActiveSessionStartedReference.current = true;
          if (activeData?.sessionId) {
            setLocalStorageItem('sessionId', activeData.sessionId);
          }

          const energyKwh = Number(activeData?.energyKwh ?? 0);
          const rawCost = activeData?.liveCost ?? activeData?.totalCost ?? 0;
          const parsedCost = Number(rawCost);
          const liveCost = Number.isFinite(parsedCost) ? parsedCost : 0;
          const durationSeconds = Number(activeData?.durationSeconds ?? 0);
          const currency = activeData?.billingCurrency || activeData?.currency || 'SAR';

          const hours = Math.floor(durationSeconds / 3600);
          const minutes = Math.floor((durationSeconds % 3600) / 60);
          const seconds = durationSeconds % 60;

          setLiveCostWatts({
            liveCost: isFreeCharging ? 0 : liveCost,
            liveWatts: energyKwh,
            currency,
            liveTime: { hours, minutes, seconds },
          });

          if (Array.isArray(activeData?.chargingPeriods) && activeData.chargingPeriods.length > 0) {
            const chartPoints: SocKwApiPoint[] = activeData.chargingPeriods.map((period: any) => {
              const ts = period.start_date_time;
              const powerDim = period.dimensions?.find(
                (dimensionItem: any) => dimensionItem.type === 'POWER',
              );
              const socDim = period.dimensions?.find(
                (dimensionItem: any) => dimensionItem.type === 'STATE_OF_CHARGE',
              );
              const energyDim = period.dimensions?.find(
                (dimensionItem: any) => dimensionItem.type === 'ENERGY',
              );

              const kw = Number(powerDim?.volume ?? energyDim?.volume ?? 0);
              const soc = Number(socDim?.volume ?? activeData?.soc ?? 0);

              return { ts, kw, soc };
            });
            setRealTimeChargingData(chartPoints);
          } else if (activeData?.soc !== undefined && activeData?.soc !== null) {
            appendSocPoint({
              ts: activeData?.lastUpdated || new Date().toISOString(),
              kw: energyKwh,
              soc: Number(activeData.soc),
            });
          }

          setIsConnecting(false);
          setIsConnectionTimeout(false);
          setStartCharging(true);
          removeLocalStorageItem('connectionStartTime');
          removeLocalStorageItem('commandId');

          if (
            activeData?.status === 'finished' ||
            activeData?.status === 'Completed' ||
            activeData?.status === 'Finished' ||
            activeData?.status === 'Stopped' ||
            activeData?.isCompleted
          ) {
            hasActiveSessionStartedReference.current = false;
            setStartCharging(false);
            setIsConnecting(false);
            setIsCommandAccepted(false);
            handleChargingStop(activeData);
          }
        } else if (rawData === null && hasActiveSessionStartedReference.current) {
          hasActiveSessionStartedReference.current = false;
          setStartCharging(false);
          setIsConnecting(false);
          setIsCommandAccepted(false);
          handleChargingStop({});
        }
      } catch (error) {
        console.error('Error in live commercial transaction poll:', error);
      }
    };

    pollLiveCommercialData();
    const pollInterval = startCharging ? 5000 : 2000;
    pollingTimer = setInterval(pollLiveCommercialData, pollInterval);

    return () => {
      if (pollingTimer) {
        clearInterval(pollingTimer);
      }
    };
  }, [startCharging, isCommandAccepted, appendSocPoint, handleChargingStop]);

  // Browser Tab Visibility & Focus catch-up
  useEffect(() => {
    /**
     * Handles browser tab visibility change and focus events
     */
    const handleVisibilityOrFocus = () => {
      if (document.visibilityState === 'visible') {
        // User returned to tab or unlocked device; trigger immediate live session poll
        if (startCharging || isCommandAccepted) {
          charger
            .getTransaction({})
            .then((response) => {
              const rawData = response?.data?.data || response?.data;
              if (
                rawData?.active === null ||
                (rawData && 'active' in rawData && rawData.active === null)
              ) {
                if (hasActiveSessionStartedReference.current) {
                  hasActiveSessionStartedReference.current = false;
                  setStartCharging(false);
                  setIsConnecting(false);
                  setIsCommandAccepted(false);
                  handleChargingStop(rawData?.summary || rawData?.lastSession || rawData);
                }
              }
            })
            .catch((error) => console.warn('Error on visibility catch-up:', error));
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityOrFocus);
    window.addEventListener('focus', handleVisibilityOrFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
      window.removeEventListener('focus', handleVisibilityOrFocus);
    };
  }, [startCharging, isCommandAccepted, handleChargingStop]);

  /**
   * Handles unexpected charging stop
   */
  const handleUnexpectedStop = () => {
    if (commandPollTimerReference.current) {
      clearTimeout(commandPollTimerReference.current);
      commandPollTimerReference.current = null;
    }
    if (stopPollTimerReference.current) {
      clearTimeout(stopPollTimerReference.current);
      stopPollTimerReference.current = null;
    }
    setIsStopping(false);
    hasActiveSessionStartedReference.current = false;
    setIsCommandAccepted(false);
    removeLocalStorageItem('guestToken');
    removeLocalStorageItem('selectedConnector');
    removeLocalStorageItem('commandId');
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: '',
      content: (
        <div className="modal-error-message">
          <div className="error-content">
            <span>
              <TimerIcon />
            </span>
            <p>{t('unexpectedStop')}</p>
          </div>
          <p className="description">Something went wrong. Please try again.</p>
          <MyButton label={t('chargeAgain')} buttonType="submit" onClickFunc={handleBackToStart} />
        </div>
      ),
      notificationModal: true,
    });
  };

  /**
   * Handles stop charging API call with multi-stage confirmation:
   * 1. Dispatch remote-stop
   * 2. Await command status ACCEPTED (every 2s, ≤60s)
   * 3. Confirm stopped via live endpoint active == null (every 2s, ≤60s)
   */
  const handleStopCharging = async () => {
    if (isStopping) return;

    const sessionId = getLocalStorageItem('sessionId') || '';

    if (!sessionId) {
      setToastStatus({
        openToast: true,
        showToastMessage: 'Session details not found. Please refresh.',
      });
      return;
    }

    setIsStopping(true);

    try {
      const response = await charger.stopCharging({
        sessionId,
      });

      const responseData = response?.data?.data || response?.data;

      // 1. Check for synchronous rejection
      if (
        responseData?.result === 'REJECTED' ||
        responseData?.status === 'REJECTED' ||
        responseData?.result === 'FAILED'
      ) {
        setIsStopping(false);
        setToastStatus({
          openToast: true,
          showToastMessage:
            responseData?.message ||
            t('stopCommandRejected') ||
            'Charger rejected the stop command. Charging is still active.',
        });
        return;
      }

      const stopCommandId =
        responseData?.commandId ??
        responseData?.command_id ??
        responseData?.data?.commandId ??
        responseData?.data?.command_id;

      /**
       * Stage 2: Poll live endpoint until active === null (up to 60s)
       */
      const pollLiveUntilStopped = () => {
        let liveAttempts = 0;
        const maxLiveAttempts = 30; // 30 * 2s = 60s

        /**
         * Checks if live session is null / stopped
         */
        const checkLiveNull = async () => {
          if (isCancelledReference.current) return;

          try {
            const liveResponse = await charger.getTransaction({});
            const liveRawData = liveResponse?.data?.data || liveResponse?.data;

            const isSessionEnded =
              liveRawData?.active === null ||
              (liveRawData && 'active' in liveRawData && liveRawData.active === null) ||
              liveRawData === null ||
              liveRawData?.active?.status === 'finished' ||
              liveRawData?.active?.status === 'Completed' ||
              liveRawData?.active?.status === 'Finished' ||
              liveRawData?.active?.status === 'Stopped' ||
              liveRawData?.active?.isCompleted;

            if (isSessionEnded) {
              // Confirmed stopped by charger!
              setIsStopping(false);
              hasActiveSessionStartedReference.current = false;
              setStartCharging(false);
              setIsCommandAccepted(false);
              handleChargingStop(
                liveRawData?.summary ||
                  liveRawData?.lastSession ||
                  liveRawData?.active ||
                  liveRawData ||
                  {},
              );
              return;
            }
          } catch (liveError) {
            console.warn('Transient error checking live stop status:', liveError);
          }

          liveAttempts += 1;
          if (liveAttempts >= maxLiveAttempts) {
            // 60s timeout fallback: finalize with last known live snapshot
            setIsStopping(false);
            hasActiveSessionStartedReference.current = false;
            setStartCharging(false);
            setIsCommandAccepted(false);
            handleChargingStop({});
            return;
          }

          stopPollTimerReference.current = setTimeout(checkLiveNull, 2000);
        };

        checkLiveNull();
      };

      /**
       * Stage 1: Await command status ACCEPTED if commandId is present
       */
      if (stopCommandId) {
        let commandAttempts = 0;
        const maxCommandAttempts = 30; // 30 * 2s = 60s

        /**
         * Checks stop command status until accepted or rejected
         */
        const checkStopCommand = async () => {
          if (isCancelledReference.current) return;

          try {
            const cmdResponse = await charger.getCommandStatus(stopCommandId);
            const cmdData = cmdResponse?.data?.data || cmdResponse?.data;
            const cmdStatus = String(cmdData?.status || '').toLowerCase();
            const cmdResult = String(cmdData?.result || '').toLowerCase();

            if (
              cmdStatus === 'rejected' ||
              cmdStatus === 'failed' ||
              cmdResult === 'rejected' ||
              cmdResult === 'failed'
            ) {
              // Stop command was rejected by charger! Resume live session.
              setIsStopping(false);
              setToastStatus({
                openToast: true,
                showToastMessage:
                  cmdData?.message ||
                  t('stopCommandRejected') ||
                  'Stop command was rejected by charger.',
              });
              return;
            }

            if (
              cmdStatus === 'accept' ||
              cmdStatus === 'accepted' ||
              cmdResult === 'accepted' ||
              cmdResult === 'accept'
            ) {
              // Command acknowledged, now confirm power actually stopped
              pollLiveUntilStopped();
              return;
            }
          } catch (cmdError) {
            console.warn('Error polling stop command status:', cmdError);
          }

          commandAttempts += 1;
          if (commandAttempts >= maxCommandAttempts) {
            // Proceed to live check even if command status timed out
            pollLiveUntilStopped();
            return;
          }

          stopPollTimerReference.current = setTimeout(checkStopCommand, 2000);
        };

        checkStopCommand();
      } else {
        // No commandId returned, proceed directly to confirming live == null
        pollLiveUntilStopped();
      }
    } catch (error: any) {
      setIsStopping(false);
      handleUnexpectedStop();
      setToastStatus({
        openToast: true,
        showToastMessage: error?.response?.data?.message || t('somethingWrong'),
      });
    }
  };

  /**
   * Opens stop charging confirmation modal
   */
  const openStopChargingConfirmModal = () => {
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: '',
      content: (
        <div className="modal-error-message">
          <div className="error-content">
            <span>
              <TimerIcon />
            </span>
            <p>{t('stopChargingMessage') || 'Are you sure you want to stop charging?'}</p>
          </div>
          <div className="modal-button-wrap">
            <MyButton
              label={t('cancel') || 'Cancel'}
              buttonType="active-outline-error"
              onClickFunc={() => {
                setModalBoxStatus('close');
              }}
            />
            <MyButton
              label={t('confirm') || 'Confirm'}
              buttonType="submit"
              loading={isStopping}
              disableButton={isStopping}
              onClickFunc={() => {
                setModalBoxStatus('close');
                handleStopCharging();
              }}
            />
          </div>
        </div>
      ),
      notificationModal: false,
    });
  };

  /**
   * Refreshes charging session status
   */
  const handlePageRefresh = async () => {
    setRefreshLoading(true);
    try {
      const response = await charger.getTransaction({});

      const rawData = response?.data?.data || response?.data;

      // When active is null, only proceed to invoice if the session had already started/been active
      if (rawData?.active === null || (rawData && 'active' in rawData && rawData.active === null)) {
        if (hasActiveSessionStartedReference.current) {
          hasActiveSessionStartedReference.current = false;
          setStartCharging(false);
          handleChargingStop(rawData?.summary || rawData?.lastSession || rawData);
        }
        return;
      }

      const activeData = rawData?.active || rawData;

      if (activeData) {
        hasActiveSessionStartedReference.current = true;
        if (activeData?.sessionId) {
          setLocalStorageItem('sessionId', activeData.sessionId);
        }

        const energyKwh = Number(activeData?.energyKwh ?? 0);
        const rawCost = activeData?.liveCost ?? activeData?.totalCost ?? 0;
        const parsedCost = Number(rawCost);
        const liveCost = Number.isFinite(parsedCost) ? parsedCost : 0;
        const durationSeconds = Number(activeData?.durationSeconds ?? 0);
        const currency = activeData?.billingCurrency || activeData?.currency || 'SAR';

        const hours = Math.floor(durationSeconds / 3600);
        const minutes = Math.floor((durationSeconds % 3600) / 60);
        const seconds = durationSeconds % 60;

        setLiveCostWatts({
          liveCost: isFreeCharging ? 0 : liveCost,
          liveWatts: energyKwh,
          currency,
          liveTime: { hours, minutes, seconds },
        });

        if (Array.isArray(activeData?.chargingPeriods) && activeData.chargingPeriods.length > 0) {
          const chartPoints: SocKwApiPoint[] = activeData.chargingPeriods.map((period: any) => {
            const ts = period.start_date_time;
            const powerDim = period.dimensions?.find(
              (dimensionItem: any) => dimensionItem.type === 'POWER',
            );
            const socDim = period.dimensions?.find(
              (dimensionItem: any) => dimensionItem.type === 'STATE_OF_CHARGE',
            );
            const energyDim = period.dimensions?.find(
              (dimensionItem: any) => dimensionItem.type === 'ENERGY',
            );

            const kw = Number(powerDim?.volume ?? energyDim?.volume ?? 0);
            const soc = Number(socDim?.volume ?? activeData?.soc ?? 0);

            return { ts, kw, soc };
          });
          setRealTimeChargingData(chartPoints);
        } else if (activeData?.soc !== undefined && activeData?.soc !== null) {
          appendSocPoint({
            ts: activeData?.lastUpdated || new Date().toISOString(),
            kw: energyKwh,
            soc: Number(activeData.soc),
          });
        }

        if (activeData?.status === 'inProgress') {
          setStartCharging(true);
          setLoader(false);
        } else if (
          activeData?.status === 'finished' ||
          activeData?.status === 'Completed' ||
          activeData?.status === 'Finished' ||
          activeData?.status === 'Stopped' ||
          activeData?.isCompleted
        ) {
          hasActiveSessionStartedReference.current = false;
          setStartCharging(false);
          handleChargingStop(activeData);
        }
      } else if (rawData === null && hasActiveSessionStartedReference.current) {
        hasActiveSessionStartedReference.current = false;
        setStartCharging(false);
        handleChargingStop({});
      }
    } catch (error: any) {
      setToastStatus({
        openToast: true,
        showToastMessage: error?.response?.data?.message || 'Session error',
      });
    } finally {
      setRefreshLoading(false);
    }
  };

  useEffect(() => {
    const savedIdle = getLocalStorageItem('idleState');
    if (!savedIdle) return;

    setActionRequired(true);
    setPenaltyTransactionStartTime(savedIdle.penaltyTransactionStartTime);
    setDurationAmountList(savedIdle.durationAmountList);
    setCeilingType(savedIdle.ceilingType);
    setCeilingValue(savedIdle.ceilingValue);
    setTotalAmount(savedIdle.totalAmount);
  }, []);

  useEffect(() => {
    /**
     * Warns user before leaving if charging is pending
     * @param event BeforeUnload event
     */
    const handleBeforeUnload = async (event: BeforeUnloadEvent) => {
      if (loader && !startCharging) {
        event.preventDefault();
        // eslint-disable-next-line no-param-reassign
        event.returnValue = t('leaveWarning');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [loader, startCharging, t]);

  useEffect(() => {
    if (!selectedConnectorId && connectors.length > 0) {
      setSelectedConnectorId(connectors[0].connectorId);
    }
  }, [selectedConnectorId, connectors]);

  useEffect(() => {
    if (!actionRequired || !penaltyTransactionStartTime) {
      return undefined;
    }

    const interval = setInterval(() => {
      const elapsedMs = Date.now() - new Date(penaltyTransactionStartTime).getTime();
      const hours = Math.floor(elapsedMs / 3600000);
      const minutes = Math.floor((elapsedMs % 3600000) / 60000);
      const seconds = Math.floor((elapsedMs % 60000) / 1000);

      setIdleElapsedTime(
        `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
          2,
          '0',
        )}:${String(seconds).padStart(2, '0')}`,
      );
      const elapsedMinutes = getElapsedMinutes(penaltyTransactionStartTime);
      setIdleAmount(
        calculateIdleFeeAmount(
          elapsedMinutes,
          durationAmountList,
          ceilingType,
          ceilingValue,
          totalAmount,
        ),
      );
      setIdleMode(getCurrentIdleMode(elapsedMinutes, durationAmountList));
    }, 1000);

    return () => clearInterval(interval);
  }, [
    actionRequired,
    penaltyTransactionStartTime,
    durationAmountList,
    ceilingValue,
    ceilingType,
    totalAmount,
  ]);

  return (
    <div className="charging-status-wrap">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={!modalBoxDetail.notificationModal}
      />
      <ToastMessage
        open={toastStatus.openToast}
        onClose={() => setToastStatus({ openToast: false, showToastMessage: '' })}
        type="error"
        message={toastStatus.showToastMessage || t('somethingWentWrong')}
        onCloseTimer={6000}
      />

      {/* --- PRE-CHARGE CONFIGURATION SCREEN (Separate Component) --- */}
      {!startCharging && !actionRequired && !isConnecting && !isConnectionTimeout && (
        <PreChargeConfig
          connectors={connectors}
          selectedConnectorId={selectedConnectorId}
          paymentDetails={paymentDetails}
          currentPrice={currentPrice}
          vat={vat ?? fees?.vat}
          loader={loader}
          onProceedToCharge={handleStartCharging}
          navigateToPaymentMethod={navigateToPaymentMethod}
          fees={fees}
          evseUid={deviceId}
          isFreeCharging={isFreeCharging}
        />
      )}

      {/* --- CONNECTING / 90s COUNTDOWN SCREEN --- */}
      {isConnecting && !startCharging && !actionRequired && (
        <div className="charging-status-container unplug connecting-session-container">
          <div className="charging-status-title-wrap">
            <div style={{ width: 24 }} />
            <h1>{t('connectingToCharger') || 'Connecting to Charger'}</h1>
            <div style={{ width: 24 }} />
          </div>

          <div className="charging-loader-wrap">
            <div className="charging-battery">
              <BatteryLoader loading />
            </div>
            <img src={ChargerBg} alt="" className="charger-bg-image" />
          </div>

          <div className="connecting-info-card">
            <div className="countdown-timer-chip">
              <ClockIcon className="icon" />
              <span>
                {String(Math.floor(connectionTimer / 60)).padStart(2, '0')}:
                {String(connectionTimer % 60).padStart(2, '0')}
              </span>
            </div>
            <h3>{t('startingChargingSession') || 'Initializing Charger...'}</h3>
            <p className="connecting-desc">
              {t('connectingDesc') ||
                'Please wait while communication is established with the EV charger. This may take up to 90 seconds.'}
            </p>
            <p className="connecting-plug-hint">
              {t('connectingCheckPlug') ||
                'Please make sure the connector is firmly plugged into your vehicle.'}
            </p>
          </div>

          <div className="button-desc-wrap">
            <div className="app-version">
              <span>{appVersion}</span>
            </div>
          </div>
        </div>
      )}

      {/* --- CONNECTION TIMEOUT SCREEN (90s Completed - Show Start Again) --- */}
      {isConnectionTimeout && !startCharging && !actionRequired && (
        <div className="charging-status-container unplug timeout-session-container">
          <div className="charging-status-title-wrap">
            <span role="presentation" onClick={handleRestartConnection}>
              <NavigateBack />
            </span>
            <h1>{t('connectionFailed') || 'Connection Timeout'}</h1>
            <div style={{ width: 24 }} />
          </div>

          <div className="timeout-action-card">
            <div className="icon">
              <AlertV1Icon />
            </div>
            <h3>{t('sessionStartTimeout') || 'Charger Did Not Start'}</h3>
            <p className="timeout-desc">
              {t('sessionStartTimeoutDesc') ||
                'The charger did not respond within 90 seconds. Please verify that the charging connector is firmly plugged in and try again.'}
            </p>
          </div>

          <div className="button-desc-wrap">
            <div className="app-version">
              <span>{appVersion}</span>
            </div>
          </div>
        </div>
      )}

      {/* --- LIVE CHARGING MONITORING SCREEN (when startCharging === true) --- */}
      {startCharging && (
        <div className={`charging-status-container ${actionRequired ? '' : 'unplug'}`}>
          <div className="charging-status-title-wrap">
            <div style={{ width: 24 }} />
            <h1>{t('chargingSession')}</h1>
            <div className="refresh-charge-session">
              <span
                className={refreshLoading ? 'refresh-icon' : ''}
                role="presentation"
                onClick={handlePageRefresh}>
                <RefreshIcon />
              </span>
            </div>
          </div>

          <div className="charging-loader-wrap">
            <div className="charging-battery">
              <BatteryLoader loading={startCharging} />
            </div>
            <img src={ChargerBg} alt="" className="charger-bg-image" />
          </div>

          {liveCostWatts && (
            <div className="live-info">
              <div className="live-info__item">
                <p className="label">{t('liveWatts') || 'Live Energy'}</p>
                <p className="value">
                  <EnergyIcon className="icon" />
                  <span>{liveCostWatts.liveWatts.toFixed(3)} kWh</span>
                </p>
              </div>
              <div className="live-info__item">
                <p className="label">{t('liveCost') || 'Live Cost'}</p>
                <p className="value">
                  <PaymentIcon className="icon" />
                  <span>
                    {liveCostWatts.liveCost.toFixed(2)} {liveCostWatts.currency}
                  </span>
                </p>
              </div>
              <div className="live-info__item">
                <p className="label">{t('elapsedTime') || 'Elapsed Time'}</p>
                <p className="value">
                  <ClockIcon className="icon" />
                  <span>
                    {String(liveCostWatts.liveTime.hours).padStart(2, '0')}:
                    {String(liveCostWatts.liveTime.minutes).padStart(2, '0')}:
                    {String(liveCostWatts.liveTime.seconds).padStart(2, '0')}
                  </span>
                </p>
              </div>
            </div>
          )}

          <div className="charging-speed-soc-graph">
            <ChargingSpeedSocChart data={realTimeChargingData} available />
          </div>

          <div className="button-desc-wrap">
            <MyButton
              label={
                isStopping
                  ? t('stoppingCharging') || 'Stopping Charging...'
                  : t('stopCharging') || 'Stop Charging'
              }
              buttonType="error"
              loading={isStopping}
              disableButton={isStopping}
              onClickFunc={openStopChargingConfirmModal}
            />
            <p className="info-text">{t('refreshInfo')}</p>
            <div className="app-version">
              <span>{appVersion}</span>
            </div>
          </div>
        </div>
      )}

      {/* --- IDLE FEE UNPLUG CARD --- */}
      {actionRequired && (
        <div className="charging-status-container-unplug">
          <div className="idle-fee-card">
            <div className="idle-fee-alert">
              <div className="title">
                <AlertV1Icon />
                {t('idleFeeActive')}
              </div>
              <p className="desc">{t('idleFeeActiveDesc')}</p>
            </div>

            <div className="action-card">
              <div className="icon">
                <UnplugIcon />
              </div>

              <p className="action-title">{t('actionRequired')}</p>
              <h3>{t('unplugConnector')}</h3>
              <p className="action-desc">{t('unplugDesc')}</p>

              <div className="charge-info">
                <p className="ammount">
                  {t('chargedAmount')} {idleAmount} {t('currency')}
                </p>

                <div className="flate-rate-content">
                  <p>{getIdleModeLabel(idleMode, t)}</p>
                  <p className="time">{idleElapsedTime}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChargingStatus;
